import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// ============================================================
// CART CONTEXT
// ============================================================

const CartContext = createContext(null);

// ============================================================
// HOOK
// ============================================================

export const useCart = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error(
      "useCart must be used inside a CartProvider"
    );
  }

  return context;
};

// ============================================================
// PROVIDER
// ============================================================

export const CartProvider = ({ children }) => {
  // ==========================================================
  // LOAD CART
  // ==========================================================

  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem("cart");

      if (!savedCart) {
        return [];
      }

      const parsedCart = JSON.parse(savedCart);

      return Array.isArray(parsedCart)
        ? parsedCart
        : [];
    } catch (error) {
      console.error(
        "CartContext: Failed to parse saved cart:",
        error
      );

      localStorage.removeItem("cart");

      return [];
    }
  });

  // ==========================================================
  // WARNING
  // ==========================================================

  const [warning, setWarning] = useState(false);

  // ==========================================================
  // SAVE CART TO LOCAL STORAGE
  // ==========================================================

  useEffect(() => {
    try {
      localStorage.setItem(
        "cart",
        JSON.stringify(cart)
      );
    } catch (error) {
      console.error(
        "CartContext: Failed to save cart:",
        error
      );
    }
  }, [cart]);

  // ==========================================================
  // ADD TO CART
  // ==========================================================

  const addToCart = useCallback((product) => {
    if (!product?._id) {
      console.warn(
        "CartContext: Cannot add product without _id."
      );

      return;
    }

    setCart((previousCart) => {
      const exists = previousCart.some(
        (item) => item?._id === product._id
      );

      // ------------------------------------------------------
      // PREVENT DUPLICATE PRODUCT
      // ------------------------------------------------------

      if (exists) {
        setWarning(true);
        return previousCart;
      }

      // ------------------------------------------------------
      // STORE COMPLETE IMAGE INFORMATION
      // ------------------------------------------------------

      const cleanItem = {
        _id: product._id,
        name: product.name || "Product",
        brand: product.brand || "",
        price: Number(product.price) || 0,
        salePrice:
          product.salePrice !== undefined &&
          product.salePrice !== null
            ? Number(product.salePrice)
            : undefined,

        // Keep the complete image array.
        images: Array.isArray(product.images)
          ? product.images
          : [],

        quantity: 1,
      };

      return [
        ...previousCart,
        cleanItem,
      ];
    });
  }, []);

  // ==========================================================
  // REMOVE FROM CART
  // ==========================================================

  const removeFromCart = useCallback((id) => {
    if (!id) {
      return;
    }

    setCart((previousCart) =>
      previousCart.filter(
        (item) => item?._id !== id
      )
    );
  }, []);

  // ==========================================================
  // INCREASE QUANTITY
  // ==========================================================

  const increaseQuantity = useCallback((id) => {
    if (!id) {
      return;
    }

    setCart((previousCart) =>
      previousCart.map((item) => {
        if (item?._id !== id) {
          return item;
        }

        const currentQuantity = Number(
          item.quantity || 1
        );

        return {
          ...item,
          quantity:
            Number.isFinite(currentQuantity)
              ? currentQuantity + 1
              : 2,
        };
      })
    );
  }, []);

  // ==========================================================
  // DECREASE QUANTITY
  // ==========================================================

  const decreaseQuantity = useCallback((id) => {
    if (!id) {
      return;
    }

    setCart((previousCart) =>
      previousCart.map((item) => {
        if (item?._id !== id) {
          return item;
        }

        const currentQuantity = Number(
          item.quantity || 1
        );

        return {
          ...item,
          quantity: Math.max(
            1,
            Number.isFinite(currentQuantity)
              ? currentQuantity - 1
              : 1
          ),
        };
      })
    );
  }, []);

  // ==========================================================
  // CLEAR / RESET CART
  // ==========================================================
  //
  // IMPORTANT:
  // This function:
  //
  // 1. Immediately changes React cart state to []
  // 2. Immediately removes localStorage cart
  // 3. Navbar re-renders with cart count = 0
  //
  // useCallback keeps the function reference stable.
  //
  // ==========================================================

  const resetCart = useCallback(() => {
    console.log(
      "CartContext: Clearing shopping cart..."
    );

    // Clear React state immediately.
    setCart([]);

    // Clear persisted cart immediately.
    try {
      localStorage.removeItem("cart");
    } catch (error) {
      console.error(
        "CartContext: Failed to remove localStorage cart:",
        error
      );
    }

    // Clear any possible warning.
    setWarning(false);
  }, []);

  // ==========================================================
  // CART ITEM COUNT
  // ==========================================================

  const cartItemCount = useMemo(() => {
    return cart.reduce((total, item) => {
      const quantity = Number(
        item?.quantity ??
          item?.qty ??
          1
      );

      return (
        total +
        (
          Number.isFinite(quantity)
            ? quantity
            : 1
        )
      );
    }, 0);
  }, [cart]);

  // ==========================================================
  // CART TOTAL
  // ==========================================================

  const cartTotal = useMemo(() => {
    return cart.reduce((total, item) => {
      const price = Number(
        item?.salePrice ??
          item?.price ??
          0
      );

      const quantity = Number(
        item?.quantity ??
          item?.qty ??
          1
      );

      const safePrice =
        Number.isFinite(price)
          ? price
          : 0;

      const safeQuantity =
        Number.isFinite(quantity)
          ? quantity
          : 1;

      return (
        total +
        safePrice * safeQuantity
      );
    }, 0);
  }, [cart]);

  // ==========================================================
  // CONTEXT VALUE
  // ==========================================================

  const contextValue = useMemo(
    () => ({
      cart,

      // Useful aliases.
      cartItems: cart,

      cartItemCount,

      cartTotal,

      warning,

      setWarning,

      addToCart,

      removeFromCart,

      increaseQuantity,

      decreaseQuantity,

      resetCart,

      // Alias in case another component expects clearCart.
      clearCart: resetCart,
    }),
    [
      cart,
      cartItemCount,
      cartTotal,
      warning,
      addToCart,
      removeFromCart,
      increaseQuantity,
      decreaseQuantity,
      resetCart,
    ]
  );

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;